import { SUNAT_CATALOGOS } from "./catalogosSunat";

const cleanText = (value) => (value == null ? "" : String(value).trim());
const cleanNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
};
const toPeriodo = (value) => cleanText(value).replace(/-/g, "").slice(0, 6);

const resolveTipoOperacion = (documento) => cleanText(documento?.tipo_operacion || "").toUpperCase();

export const buildRvieRecord = ({ documentoTributario = {}, registroTributario = {}, extra = {} } = {}) => {
  const tipoOperacion = resolveTipoOperacion(documentoTributario);
  const importes = documentoTributario.importes || {};
  
  const rawTipoComp = cleanText(documentoTributario.tipo_comprobante || documentoTributario.comprobante?.tipo || documentoTributario.tipo || "FACTURA").toUpperCase();
  const catTipoComp = SUNAT_CATALOGOS.TIPO_COMPROBANTE[rawTipoComp];
  const tipoComprobante = catTipoComp?.codigo || (rawTipoComp === "FACTURA" ? "01" : rawTipoComp === "BOLETA" ? "03" : rawTipoComp === "RECIBO" ? "14" : rawTipoComp);

  const numDocCliente = cleanText(
    documentoTributario.tercero?.numero_documento ||
    documentoTributario.cliente_numero_documento ||
    documentoTributario.cliente_ruc ||
    documentoTributario.cliente?.numeroDocumento ||
    documentoTributario.cliente?.ruc ||
    ""
  );

  let rawTipoDocCliente = cleanText(
    documentoTributario.tercero?.tipo_documento ||
    documentoTributario.cliente_tipo_documento ||
    documentoTributario.cliente?.tipoDocumento ||
    ""
  ).toUpperCase();

  let tipoDocumentoCliente = SUNAT_CATALOGOS.TIPO_DOCUMENTO[rawTipoDocCliente]?.codigo || rawTipoDocCliente;
  if (!tipoDocumentoCliente && numDocCliente) {
    tipoDocumentoCliente = numDocCliente.length === 11 ? "6" : numDocCliente.length === 8 ? "1" : "0";
  } else if (tipoDocumentoCliente === "RUC") {
    tipoDocumentoCliente = "6";
  } else if (tipoDocumentoCliente === "DNI") {
    tipoDocumentoCliente = "1";
  }

  const razonSocialCliente = cleanText(
    documentoTributario.tercero?.razon_social ||
    documentoTributario.cliente_razon_social ||
    documentoTributario.cliente?.razonSocial ||
    ""
  );

  const baseGravada = tipoOperacion === "EXONERADO" ? 0 : cleanNumber(importes.base_gravada ?? importes.subtotal ?? documentoTributario.subtotal ?? 0);
  const baseExonerada = tipoOperacion === "EXONERADO" ? cleanNumber(importes.base_exonerada ?? importes.total ?? documentoTributario.total ?? 0) : cleanNumber(importes.base_exonerada ?? 0);
  const baseInafecta = tipoOperacion === "INAFECTO" ? cleanNumber(importes.base_inafecta ?? importes.total ?? documentoTributario.total ?? 0) : cleanNumber(importes.base_inafecta ?? 0);
  const exportacion = tipoOperacion === "EXPORTACION" ? cleanNumber(importes.exportacion ?? importes.total ?? documentoTributario.total ?? 0) : cleanNumber(importes.exportacion ?? 0);

  return {
    id: cleanText(extra.id || documentoTributario.id || registroTributario.id || ""),
    documentoTributarioId: cleanText(documentoTributario.id || ""),
    registroTributarioId: cleanText(registroTributario.id || ""),
    periodo: toPeriodo(documentoTributario.periodo || documentoTributario.fecha_emision || documentoTributario.fechaEmision || ""),
    ruc: cleanText(documentoTributario.ruc_emisor || documentoTributario.ruc_empresa || extra.ruc || ""),
    tipoComprobante,
    serie: cleanText(documentoTributario.serie || documentoTributario.comprobante?.serie || ""),
    numero: cleanText(documentoTributario.numero || documentoTributario.comprobante?.numero || ""),
    fechaEmision: cleanText(documentoTributario.fecha_emision || documentoTributario.fechaEmision || ""),
    fechaVencimiento: cleanText(documentoTributario.fecha_vencimiento || documentoTributario.fecha_emision || documentoTributario.fechaEmision || ""),
    tipoDocumentoCliente,
    numeroDocumentoCliente: numDocCliente,
    razonSocialCliente,
    moneda: cleanText(documentoTributario.moneda || documentoTributario.comprobante?.moneda || "PEN").toUpperCase(),
    tipoCambio: cleanNumber(documentoTributario.tipo_cambio || documentoTributario.comprobante?.tipo_cambio || 1),
    baseGravada,
    baseExonerada,
    baseInafecta,
    exportacion,
    igv: cleanNumber(importes.igv ?? documentoTributario.igv ?? 0),
    otrosTributos: cleanNumber(importes.otros_tributos ?? documentoTributario.otrosTributos ?? 0),
    total: cleanNumber(importes.total ?? documentoTributario.total ?? 0),
    tipoOperacion,
    documentoRelacionado: documentoTributario.documento_relacionado ? {
      tipoDocumentoReferencia: cleanText(documentoTributario.documento_relacionado.tipo_documento_referencia || ""),
      serieReferencia: cleanText(documentoTributario.documento_relacionado.serie_referencia || ""),
      numeroReferencia: cleanText(documentoTributario.documento_relacionado.numero_referencia || ""),
      motivo: cleanText(documentoTributario.documento_relacionado.motivo || ""),
      tipoNota: cleanText(documentoTributario.documento_relacionado.tipo_nota || ""),
    } : null,
    estadoTributario: cleanText(documentoTributario.estado_tributario || "PENDIENTE_VALIDACION_TRIBUTARIA"),
    estadoSunat: cleanText(documentoTributario.estado_sunat || "PENDIENTE"),
    origen: documentoTributario.origen || null,
    catalogos: {
      tipoComprobante: catTipoComp || null,
      tipoDocumentoCliente: SUNAT_CATALOGOS.TIPO_DOCUMENTO[rawTipoDocCliente] || null,
      tipoOperacion: SUNAT_CATALOGOS.TIPO_OPERACION[tipoOperacion] || null,
      moneda: SUNAT_CATALOGOS.MONEDA[cleanText(documentoTributario.moneda || "PEN").toUpperCase()] || null,
    },
  };
};

export const validateRvieRecord = (record = {}) => {
  const errors = [];
  const warnings = [];

  if (!cleanText(record.ruc)) errors.push({ field: "ruc", code: "INVALID_RUC", message: "El RUC del emisor es obligatorio." });
  if (!cleanText(record.tipoComprobante)) errors.push({ field: "tipoComprobante", code: "INVALID_TYPE", message: "El tipo de comprobante es obligatorio." });
  if (!cleanText(record.serie)) errors.push({ field: "serie", code: "MISSING_SERIE", message: "La serie es obligatoria." });
  if (!cleanText(record.numero)) errors.push({ field: "numero", code: "MISSING_NUMBER", message: "El número es obligatorio." });
  if (!cleanText(record.fechaEmision)) errors.push({ field: "fechaEmision", code: "MISSING_DATE", message: "La fecha de emisión es obligatoria." });
  if (!cleanText(record.periodo)) errors.push({ field: "periodo", code: "MISSING_PERIOD", message: "El periodo es obligatorio." });
  if (!cleanText(record.tipoDocumentoCliente)) errors.push({ field: "tipoDocumentoCliente", code: "MISSING_TERCERO_DOC", message: "El tipo de documento del cliente es obligatorio." });
  if (!cleanText(record.numeroDocumentoCliente)) errors.push({ field: "numeroDocumentoCliente", code: "MISSING_TERCERO_NUMBER", message: "El número de documento del cliente es obligatorio." });
  if (!cleanText(record.tipoOperacion)) {
    errors.push({ field: "tipoOperacion", code: "MISSING_TAX_CLASS", message: "La clasificación tributaria es obligatoria." });
    warnings.push({ field: "tipoOperacion", code: "UNKNOWN_TAX_CLASS", message: "No se pudo determinar la clasificación tributaria." });
  }
  if (record.documentoRelacionado && !cleanText(record.documentoRelacionado.numeroReferencia)) {
    errors.push({ field: "documentoRelacionado.numeroReferencia", code: "MISSING_DOCUMENT_REFERENCE", message: "La nota requiere documento original." });
  }
  if (record.moneda !== "PEN" && cleanNumber(record.tipoCambio) <= 0) {
    errors.push({ field: "tipoCambio", code: "MISSING_EXCHANGE_RATE", message: "El tipo de cambio es obligatorio para moneda extranjera." });
  }
  const totalEsperado = cleanNumber(record.baseGravada + record.baseExonerada + record.baseInafecta + record.exportacion + record.igv + record.otrosTributos);
  if (Math.abs(cleanNumber(record.total) - totalEsperado) > 0.01) {
    errors.push({ field: "total", code: "TOTAL_MISMATCH", message: "El total no coincide con la sumatoria de bases e impuestos." });
  }

  return { valid: errors.length === 0, errors, warnings };
};

const formatDateSunat = (dateStr) => {
  const text = cleanText(dateStr);
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [y, m, d] = text.split("-");
    return `${d}/${m}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return text;
  return text;
};

const formatPeriodoSunat = (periodoStr) => {
  const text = cleanText(periodoStr).replace(/-/g, "");
  if (text.length === 6) return `${text}00`;
  if (text.length === 8) return text;
  return text;
};

export const formatRvieRecordToOfficialTxt = (record = {}, extra = {}) => {
  const fields = [
    cleanText(record.ruc || extra.ruc || "20609212102"),
    cleanText(extra.razonSocialEmisor || "MI BOLETA S.A.C."),
    formatPeriodoSunat(record.periodo),
    cleanText(record.carSunat || ""),
    formatDateSunat(record.fechaEmision),
    formatDateSunat(record.fechaVencimiento),
    cleanText(record.tipoComprobante || "01"),
    cleanText(record.serie),
    cleanText(record.numero),
    cleanText(record.numeroFinal || ""),
    cleanText(record.tipoDocumentoCliente || "6"),
    cleanText(record.numeroDocumentoCliente),
    cleanText(record.razonSocialCliente),
    cleanNumber(record.exportacion || 0).toFixed(2),
    cleanNumber(record.baseGravada || 0).toFixed(2),
    cleanNumber(record.descuentoBase || 0).toFixed(2),
    cleanNumber(record.igv || 0).toFixed(2),
    cleanNumber(record.descuentoIgv || 0).toFixed(2),
    cleanNumber(record.baseExonerada || 0).toFixed(2),
    cleanNumber(record.baseInafecta || 0).toFixed(2),
    cleanNumber(record.isc || 0).toFixed(2),
    cleanNumber(record.baseIVAP || 0).toFixed(2),
    cleanNumber(record.ivap || 0).toFixed(2),
    cleanNumber(record.icbper || 0).toFixed(2),
    cleanNumber(record.otrosTributos || 0).toFixed(2),
    cleanNumber(record.total || 0).toFixed(2),
    cleanText(record.moneda || "PEN").toUpperCase(),
    cleanNumber(record.tipoCambio || 1).toFixed(3),
    formatDateSunat(record.documentoRelacionado?.fechaEmisionReferencia || ""),
    cleanText(record.documentoRelacionado?.tipoDocumentoReferencia || ""),
    cleanText(record.documentoRelacionado?.serieReferencia || ""),
    cleanText(record.documentoRelacionado?.numeroReferencia || ""),
    cleanText(record.contratoId || ""),
    cleanText(record.estadoAnotacion || "1")
  ];
  return fields.join("|");
};

export const formatRvieRecordToTxt = (record = {}, extra = {}) => {
  return formatRvieRecordToOfficialTxt(record, extra);
};
